import { z } from "zod";

import { MAX_COMPETENCY_LEVELS as MAX_LEVELS_FROM_MODEL } from "@performyard/models";
import type { CreateCompetencyBody } from "@performyard/service-contracts";

export const MAX_COMPETENCY_LEVELS = MAX_LEVELS_FROM_MODEL;
export const MAX_COMPETENCY_NAME_LENGTH = 100;
export const MAX_COMPETENCY_DESCRIPTION_LENGTH = 1000;
export const MAX_LEVEL_NAME_LENGTH = 100;
export const MAX_LEVEL_DESCRIPTION_LENGTH = 1000;

/** Route path for the competencies list — used for cancel + post-submit navigation. */
export const COMPETENCIES_LIST_PATH = "/administration/competencies";

/** Strip HTML tags so we count the visible character length, not the markup. */
export const stripHtmlTags = (html: string) => html.replace(/<[^>]*>/g, "");

const descriptionField = (maxLength: number) =>
	z
		.string()
		.default("")
		.refine((val) => stripHtmlTags(val).length <= maxLength, {
			message: `Description must be ${maxLength} characters or less`,
		});

const levelSchema = z.object({
	name: z
		.string()
		.min(1, "Level name is required")
		.max(MAX_LEVEL_NAME_LENGTH, `Level name must be ${MAX_LEVEL_NAME_LENGTH} characters or less`)
		.refine((val) => val.trim().length > 0, { message: "Level name is required" }),
	description: descriptionField(MAX_LEVEL_DESCRIPTION_LENGTH),
});

export const competencyFormSchema = z.object({
	name: z
		.string()
		.min(1, "Competency name is required")
		.max(MAX_COMPETENCY_NAME_LENGTH, `Competency name must be ${MAX_COMPETENCY_NAME_LENGTH} characters or less`)
		.refine((val) => val.trim().length > 0, { message: "Competency name is required" }),
	description: descriptionField(MAX_COMPETENCY_DESCRIPTION_LENGTH),
	is_company_wide: z.boolean().default(false),
	levels: z
		.array(levelSchema)
		.max(MAX_COMPETENCY_LEVELS, `A competency can have at most ${MAX_COMPETENCY_LEVELS} levels`)
		.default([]),
});

export type CompetencyFormValues = z.infer<typeof competencyFormSchema>;

/**
 * Default values for an empty (create-mode) form.
 * Lives next to the schema so changes to either are reviewed together.
 * Note: `name: ""` is intentionally invalid against the schema — the form
 * starts in an invalid state and validates on submit.
 */
export const EMPTY_FORM_VALUES: CompetencyFormValues = {
	name: "",
	description: "",
	is_company_wide: false,
	levels: [],
};

/** Shape of a competency returned by the API that this form consumes. */
interface CompetencyForForm {
	name: string;
	description: string | null | undefined;
	is_company_wide: boolean;
	levels: Array<{ rank: number; name: string; description: string | null | undefined }>;
}

/**
 * Map API competency → initial form values.
 * Drops `rank` — array position is the single source of truth for level order.
 * Normalizes null/undefined descriptions to empty strings.
 */
export const fromCompetency = (competency: CompetencyForForm): CompetencyFormValues => ({
	name: competency.name,
	description: competency.description ?? "",
	is_company_wide: competency.is_company_wide,
	levels: competency.levels.map((level) => ({
		name: level.name,
		description: level.description ?? "",
	})),
});

/**
 * Map form values → backend request payload.
 * Stamps `rank = index + 1` so server-side sequential-rank validation always passes.
 */
export const toRequestPayload = (values: CompetencyFormValues): CreateCompetencyBody => ({
	name: values.name,
	description: values.description,
	is_company_wide: values.is_company_wide,
	levels: values.levels.map((level, index) => ({
		rank: index + 1,
		name: level.name,
		description: level.description,
	})),
});
